import type { ComponentProps } from 'preact';
import { Map, type MapLine, type MapMarker } from '../../../base/Map/Map';
import type { JourneyLeg, JourneyPlan } from '../../../../arcticapi/types';
import type { JourneyPlanSearchRequest } from '../../../../data/journeyPlanSearch';
import { createPlaceMarker } from '../../../base/Map/PlaceMarker';
import {
  createRouteLine,
  resolveRouteColor,
} from '../../../base/Map/routeLineStyle';

type Coordinate = [number, number];

const WALK_ROUTE_COLOR = '#757575';

export interface JourneyPlanMapProps extends ComponentProps<'section'> {
  plan: JourneyPlan;
  locations?: Pick<JourneyPlanSearchRequest, 'destination' | 'origin'> | null;
}

function isCoordinate(value: unknown): value is Coordinate {
  if (!Array.isArray(value) || value.length !== 2) {
    return false;
  }

  return typeof value[0] === 'number' && typeof value[1] === 'number';
}

function collectJourneyCoordinates(
  plan: JourneyPlan,
  locations?: Pick<JourneyPlanSearchRequest, 'destination' | 'origin'> | null,
): Coordinate[] {
  const coordinates: Coordinate[] = [];

  const originCoordinate = locations?.origin.coordinate;
  const destinationCoordinate = locations?.destination.coordinate;

  if (originCoordinate) {
    coordinates.push(originCoordinate);
  }

  for (const leg of plan.legs) {
    for (const coordinate of leg.path?.coordinates ?? []) {
      if (isCoordinate(coordinate)) {
        coordinates.push(coordinate);
      }
    }

    for (const stop of leg.stops ?? []) {
      if (isCoordinate(stop.location.coordinates)) {
        coordinates.push(stop.location.coordinates);
      }
    }
  }

  if (destinationCoordinate) {
    coordinates.push(destinationCoordinate);
  }

  return coordinates;
}

function buildRouteLines(plan: JourneyPlan): MapLine[] {
  return plan.legs
    .filter((leg) => leg.path?.type === 'LineString')
    .map((leg, index) =>
      createRouteLine(
        {
          id: `${leg.mode}-${leg.lineName ?? 'line'}-${index}`,
          coordinates: leg.path?.coordinates ?? [],
        },
        resolveLegRouteColor(leg),
      ),
    );
}

function resolveLegRouteColor(leg: JourneyLeg): string {
  if (leg.mode.toLowerCase() === 'walk') {
    return WALK_ROUTE_COLOR;
  }

  return resolveRouteColor(
    leg.line?.colors?.background,
    leg.line?.colors?.foreground,
  );
}

function buildEndpointMarkers(
  locations?: Pick<JourneyPlanSearchRequest, 'destination' | 'origin'> | null,
): MapMarker[] {
  const markers: MapMarker[] = [];
  const originCoordinate = locations?.origin.coordinate;
  const destinationCoordinate = locations?.destination.coordinate;

  if (originCoordinate) {
    markers.push(
      createPlaceMarker({
        id: 'journey-origin',
        coordinate: originCoordinate,
        kind: 'origin',
        name: locations?.origin.name,
      }),
    );
  }

  if (destinationCoordinate) {
    markers.push(
      createPlaceMarker({
        id: 'journey-destination',
        coordinate: destinationCoordinate,
        kind: 'destination',
        name: locations?.destination.name,
      }),
    );
  }

  return markers;
}

export function JourneyPlanMap({
  plan,
  locations,
  ...props
}: JourneyPlanMapProps) {
  const coordinates = collectJourneyCoordinates(plan, locations);
  const markers = buildEndpointMarkers(locations);

  return (
    <Map
      lines={buildRouteLines(plan)}
      markers={markers}
      fitCoordinates={coordinates}
      {...props}
    />
  );
}
